import { CSSProperties, MouseEvent, TouchEvent, useLayoutEffect, useState } from 'react';

import { NorlysColor } from '~src/branding';
import { Icon } from '~src/components/Icon';
import { useBranding } from '~src/context/BrandingContext';
import { calculateDisabledColor } from '~src/util/color';

import { SwipeButtonMovableCircle, SwipeButtonText, SwipeButtonTrack } from './style';

type SwipeButtonProps = {
  onConfirm: () => void;
  color?: string;
  text?: string;
  disabled?: boolean;
  style?: CSSProperties;
};

export const SwipeButton = (props: SwipeButtonProps) => <SwipeButtonBase {...props} />;

export const SwipeButtonNorlys = (props: SwipeButtonProps) => (
  <SwipeButtonBase color={NorlysColor.primary.acid} {...props} />
);

const SwipeButtonBase = ({ onConfirm, color: colorInput, text, disabled, style }: SwipeButtonProps) => {
  const [trackWidth, setTrackWidth] = useState<number>();
  const [positionX, setPositionX] = useState<number>();
  const [mouseDown, setMouseDown] = useState<boolean>(false);
  const [buttonSwiped, setButtonSwiped] = useState<boolean>(false);

  const { lightColor } = useBranding();

  const color = colorInput ?? lightColor;

  const disabledColor = calculateDisabledColor(color);

  useLayoutEffect(() => {
    const buttonTrackElement = document.querySelector<HTMLButtonElement>('#swipeButtonTrack');
    setTrackWidth(buttonTrackElement?.offsetWidth);
  }, []);

  const checkButtonSwipe = (swipeDistance: number) => swipeDistance >= (trackWidth ?? 0) * 0.85;

  const finishSwipe = (newValue: boolean) => {
    if (newValue) {
      onConfirm();
    }
    setButtonSwiped(false);
    setPositionX(0);
  };

  const onTouchMove = (event: TouchEvent) => {
    if (disabled) {
      return;
    }
    const position = event.targetTouches[0].clientX;

    if (position < (trackWidth ?? 0) * 0.35) {
      return;
    }
    setPositionX(position);
    setButtonSwiped(checkButtonSwipe(position));
  };

  const onTouchEnd = () => {
    finishSwipe(buttonSwiped);
  };

  const onMouseDown = () => setMouseDown(true);

  const onMouseLeave = () => {
    setButtonSwiped(false);
    setMouseDown(false);
    setPositionX(0);
  };

  const onMouseMove = (event: MouseEvent) => {
    if (disabled) {
      return;
    }
    const position = event.clientX;

    if (position < (trackWidth ?? 0) * 0.35) {
      return;
    }
    if (mouseDown) {
      setPositionX(position);
    }
    setButtonSwiped(checkButtonSwipe(position));
  };

  const onMouseUp = () => {
    setMouseDown(false);
    finishSwipe(buttonSwiped);
  };

  return (
    <SwipeButtonTrack {...{ id: 'swipeButtonTrack', color, disabled, style }}>
      <SwipeButtonText {...{ buttonSwiped }}>
        {buttonSwiped ? 'Slip for at bekræfte' : text ?? 'Modtag tilbud'}
      </SwipeButtonText>
      <SwipeButtonMovableCircle
        {...{
          onTouchMove,
          onTouchEnd,
          onMouseDown,
          onMouseMove,
          onMouseUp,
          onMouseLeave,
          positionX,
          disabled,
          trackWidth,
        }}
      >
        <Icon.Chevron height='80%' width='80%' direction='right' color={disabled ? disabledColor : color} />
      </SwipeButtonMovableCircle>
    </SwipeButtonTrack>
  );
};
